import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface AiItemsListProps<T extends { id: string }> {
  items: T[];
  selectedId: string | null;
  getItemTitle: (item: T) => string;
  getItemSubtitle?: (item: T) => string | null;
  getItemIcon?: (item: T, isDisabled: boolean) => ReactNode;
  onSelect: (item: T) => void;
  isItemDisabled?: (item: T, index: number) => boolean;
  tooltipMessage?: string;
}

export function AiItemsList<T extends { id: string }>({
  items,
  selectedId,
  getItemTitle,
  getItemSubtitle,
  getItemIcon,
  onSelect,
  isItemDisabled,
  tooltipMessage,
}: AiItemsListProps<T>) {
  const { t } = useTranslation('common');
  const [tooltipId, setTooltipId] = useState<string | null>(null);
  const finalTooltipMessage = tooltipMessage ?? t('aiItemsList.tooltip');

  return (
    <>
      {items.map((item, index) => {
        const isActive = selectedId === item.id;
        const isDisabled = isItemDisabled ? isItemDisabled(item, index) : false;

        return (
          <button
            key={item.id}
            onClick={() => !isDisabled && onSelect(item)}
            onMouseEnter={() => isDisabled && setTooltipId(item.id)}
            onMouseLeave={() => setTooltipId(null)}
            disabled={isDisabled}
            className="relative text-left"
          >
            <div className={`${isActive
              ? 'bg-[rgba(33,151,241,0.2)]'
              : isDisabled
                ? 'bg-[#f4f4f3] cursor-not-allowed'
                : 'bg-[#f4f4f3] hover:bg-[#eeeeee]'
              } rounded-[16px] px-[24px] py-[16px] flex items-center gap-[16px] transition-colors`}>
              {/* Optional leading icon */}
              {getItemIcon && (
                <div className="w-[24px] h-[24px] flex-shrink-0">
                  {getItemIcon(item, isDisabled)}
                </div>
              )}

              {/* Content */}
              <div className="flex-1">
                <p className={`font-segoe text-[16px] leading-[24px] ${isDisabled ? 'text-[#cecdc8]' : 'text-[#0d0d0d]'}`}>
                  {getItemTitle(item)}
                </p>
                {getItemSubtitle && getItemSubtitle(item) && (
                  <p className="font-segoe text-[14px] leading-[20px] text-[#565858]">
                    {getItemSubtitle(item)}
                  </p>
                )}
              </div>

              {/* Arrow Icon */}
              <div className="w-[24px] h-[24px] flex-shrink-0 rotate-90 -scale-y-100">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19.92 8.95L13.4 15.47C12.63 16.24 11.37 16.24 10.6 15.47L4.08 8.95" stroke={isDisabled ? "#cecdc8" : "#0d0d0d"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10" />
                </svg>
              </div>
            </div>

            {tooltipId === item.id && isDisabled && (
              <div className="absolute top-1/2 left-[calc(100%+16px)] -translate-y-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                  {finalTooltipMessage}
                </p>
              </div>
            )}
          </button>
        );
      })}
    </>
  );
}
